import styled from '@emotion/styled'
import { ChangeEventHandler, ReactNode, forwardRef } from 'react'
import ReactDatePicker from 'react-datepicker'
import { z } from 'zod'

import { Input, InputProps } from './Input'
import { DATE_FNS_LOCALE_OPTIONS } from '../../constants/date'
import { useValidate } from '../../hooks'
import { useLocale, useTranslations } from '../../translate'
import { StyleProps, createStyles } from '../../utils'
import { IconName } from '../Icon'

const DATE_PICKER_ICON =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjBweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjBweCIgZmlsbD0iIzAwMDAwMCI+CiAgICA8cGF0aCBkPSJNMCAwaDI0djI0SDB6IiBmaWxsPSJub25lIiAvPgogICAgPHBhdGggZD0iTTE5IDNoLTFWMWgtMnYySDhWMUg2djJINWMtMS4xMSAwLTEuOTkuOS0xLjk5IDJMMyAxOWMwIDEuMS44OSAyIDIgMmgxNGMxLjEgMCAyLS45IDItMlY1YzAtMS4xLS45LTItMi0yem0wIDE2SDVWOGgxNHYxMXpNNyAxMGg1djVIN3oiIC8+Cjwvc3ZnPg=='

const DATE_RANGE_PICKER_ICON =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjBweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjBweCIgZmlsbD0iIzAwMDAwMCI+PHBhdGggZD0iTTAgMGgyNHYyNEgweiIgZmlsbD0ibm9uZSIvPjxwYXRoIGQ9Ik05IDExSDd2Mmgydi0yem00IDBoLTJ2Mmgydi0yem00IDBoLTJ2Mmgydi0yem0yLTdoLTFWMmgtMnYySDhWMkg2djJINWMtMS4xMSAwLTEuOTkuOS0xLjk5IDJMMyAyMGMwIDEuMS44OSAyIDIgMmgxNGMxLjEgMCAyLS45IDItMlY2YzAtMS4xLS45LTItMi0yem0wIDE2SDVWOWgxNHYxMXoiLz48L3N2Zz4K'

const DatePickerElement = styled.div<{ styles?: StyleProps }>((props) => ({
  display: 'flex',
  flexDirection: 'column',
  ...createStyles(props.styles),
}))

const toDate = (date?: DateInput) => {
  return typeof date === 'number' ? new Date(date) : date
}

const DatePickerInput = forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'onChange'> & {
    onChange?: ChangeEventHandler<HTMLInputElement>
    onDateChange: (startDate: Date | null, endDate: Date | null) => void
    range: boolean
  }
>(({ onDateChange, ...props }, ref) => {
  return (
    <Input
      {...props}
      icon={props.range ? DATE_RANGE_PICKER_ICON : DATE_PICKER_ICON}
      innerRef={ref}
      nativeOnChange={(event) => {
        const date = new Date(event.target.value)

        // Update the date only if it's a valid date:
        if (!Number.isNaN(date.getTime())) {
          onDateChange(date, date)
        }

        props.onChange?.(event)
      }}
      onChange={undefined}
    />
  )
})
DatePickerInput.displayName = 'DatePickerInput'

export type DateInput = Date | number | null

export type DatePickerProps = {
  className?: string
  /**
   * @deprecated Date format should not be freely configurable in text form.
   */
  dateFormat?: string
  /**
   * TODO(rb): rename to `labelDescription` (or remove entirely)
   */
  description?: string
  disabled?: boolean
  endDate?: DateInput
  id?: string
  label?: string
  labelIcon?: IconName
  maxDate?: DateInput
  minDate?: DateInput
  onChange: (startDate: Date | null, endDate: Date | null) => void
  placeholder?: string
  range?: boolean
  inline?: boolean
  required?: boolean
  showYearPicker?: boolean
  startDate?: DateInput
  styles?: StyleProps
  inputStyles?: StyleProps
  value?: DateInput
  schema?: z.ZodTypeAny
  errorMessage?: string
  reserveErrorSpace?: boolean
  forceShowError?: boolean
  input?: ReactNode
}

export const DatePicker = (props: DatePickerProps) => {
  const locale = useLocale()
  const t = useTranslations()

  const localeOptions = DATE_FNS_LOCALE_OPTIONS[locale]

  const handleChange = (date: Date | [Date | null, Date | null] | null) => {
    const [startDate, endDate] = Array.isArray(date) ? date : [date, date]
    props.onChange(startDate, endDate)
  }

  const validateError = useValidate(
    props.schema,
    props.required,
    props.range
      ? props.startDate && props.endDate
        ? [props.startDate, props.endDate]
        : null
      : props.value,
  )

  const customInput = (
    <DatePickerInput
      range={!!props.range}
      description={props.description}
      label={props.label}
      labelIcon={props.labelIcon}
      onDateChange={props.onChange}
      required={props.required}
      errorMessage={props.errorMessage ?? validateError}
      reserveErrorSpace={props.reserveErrorSpace}
      forceShowError={props.forceShowError}
      styles={props.inputStyles}
    />
  )

  const dateFormat =
    props.dateFormat ??
    (props.showYearPicker
      ? 'yyyy'
      : props.range
        ? localeOptions.dateFormat.short
        : localeOptions.dateFormat.long)

  return (
    <DatePickerElement className={props.className} styles={props.styles}>
      <ReactDatePicker
        id={props.id}
        inline={props.inline}
        customInput={props.input ?? (props.inline ? undefined : customInput)}
        dateFormat={dateFormat}
        disabled={props.disabled}
        endDate={toDate(props.endDate)}
        locale={localeOptions.locale}
        maxDate={toDate(props.maxDate)}
        minDate={toDate(props.minDate)}
        monthsShown={props.range ? 2 : 1}
        onChange={handleChange}
        placeholderText={props.placeholder ?? `${t.form.select_a_date()}...`}
        required={props.required}
        selected={toDate(props.value)}
        selectsRange={props.range}
        showWeekNumbers
        showYearPicker={props.showYearPicker}
        startDate={toDate(props.startDate)}
      />
    </DatePickerElement>
  )
}
